/**
 * FERP Portal - Primitive Form Schema Hook
 *
 * Bridges PrimitiveType → backend FormSchema API.
 * Applies display mode filtering using existing field metadata
 * (minimumTier, required, order).
 *
 * Architecture:
 * ```
 * usePrimitiveFormSchema(primitiveType, { displayMode })
 *   → useFormSchema({ subgraph: 'core', entityType })
 *   → schemaToFormDefinition()
 *   → filter fields by displayMode
 *   → FormDefinition (for FormRenderer)
 * ```
 *
 * NO FALLBACK: If backend FormSchema is not registered for a primitive,
 * formDefinition will be null and the UI shows "not available".
 * All field definitions MUST come from the backend (DRR-055).
 */

import { useMemo } from 'react';
import type { PrimitiveType } from '@/config/primitive-metadata';
import { useFormSchema } from '@/components/UniversalEntityForm/useFormSchema';
import { schemaToFormDefinition } from '@/components/UniversalEntityForm/schemaToFormDef';
import type { FormDefinition, FieldDefinition } from '@/renderer/types';
import { ModuleTier } from '@/generated/graphql';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Display modes for field visibility filtering.
 * These filter fields using existing backend metadata (required, minimumTier).
 */
export type FieldDisplayMode = 'essential' | 'recommended' | 'all';

export interface UsePrimitiveFormSchemaOptions {
  /** Which fields to show. Default: 'recommended' */
  displayMode?: FieldDisplayMode;
  /** Form mode affects field visibility (create vs edit) */
  mode?: 'create' | 'edit' | 'view';
  /** DRR-162: RuleSet ID for service-line-specific form resolution.
   * Pass from constellation instance's primary_ruleset. */
  rulesetId?: string;
}

export interface UsePrimitiveFormSchemaResult {
  /** The processed form definition, or null if not available */
  formDefinition: FormDefinition | null;
  /** Whether the form schema is loading from backend */
  loading: boolean;
  /** Error from backend query */
  error: Error | undefined;
  /** Refetch the schema */
  refetch: () => void;
}

// ============================================================================
// DISPLAY MODE FILTERING
// ============================================================================

/**
 * Tier values considered "basic" for the recommended display mode.
 * Fields with these tiers (or no tier) show in recommended mode.
 */
const BASIC_TIERS = new Set([ModuleTier.FREE, ModuleTier.BASIC]);

/**
 * Filter fields based on display mode using existing backend metadata.
 *
 * - essential: Only required fields
 * - recommended: Required fields + fields with FREE/BASIC tier (default)
 * - all: All fields from schema
 */
function filterFieldsByDisplayMode(
  fields: FieldDefinition[],
  displayMode: FieldDisplayMode,
): FieldDefinition[] {
  if (displayMode === 'all') {
    return fields;
  }

  return fields.filter((field) => {
    if (displayMode === 'essential') {
      return field.validation?.required === true;
    }

    // 'recommended': required OR basic tier (or no tier set = show by default)
    if (field.validation?.required) return true;

    const tier = field.metadata?.minimumTier as string | undefined;
    if (!tier) return true; // No tier restriction = show in recommended
    return BASIC_TIERS.has(tier as typeof ModuleTier.FREE);
  });
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to get a FormDefinition for a FERP primitive type.
 *
 * Uses the backend FormSchema API (DRR-055) as the sole source of truth.
 * Returns null if no backend schema is registered — frontend does NOT
 * generate any fallback fields.
 *
 * Applies client-side display mode filtering on the backend-provided fields.
 *
 * @example
 * ```tsx
 * const { formDefinition, loading } = usePrimitiveFormSchema('policy', {
 *   displayMode: 'essential',
 * });
 *
 * if (formDefinition) {
 *   return <FormRenderer form={formDefinition} onSubmit={handleSubmit} />;
 * }
 * ```
 */
export function usePrimitiveFormSchema(
  primitiveType: PrimitiveType,
  options: UsePrimitiveFormSchemaOptions = {},
): UsePrimitiveFormSchemaResult {
  const { displayMode = 'recommended', rulesetId } = options;

  // Map primitive type to backend FormSchema query params
  const entityType = primitiveType.charAt(0).toUpperCase() + primitiveType.slice(1);

  // Query backend FormSchema (DRR-162: pass rulesetId for RS-scoped override resolution)
  const { schema, loading, error, refetch } = useFormSchema({
    subgraph: 'core',
    entityType,
    rulesetId,
  });

  // Convert and filter the schema
  const formDefinition = useMemo(() => {
    // Still loading or no backend schema registered
    if (loading || !schema) {
      return null;
    }

    // Convert backend schema to FormDefinition
    const formDef = schemaToFormDefinition(schema);

    // Apply display mode filtering
    if (formDef.fields) {
      formDef.fields = filterFieldsByDisplayMode(formDef.fields, displayMode);
    }

    // Also filter fields within steps if wizard layout
    if (formDef.steps) {
      formDef.steps = formDef.steps.map((step) => ({
        ...step,
        fields: filterFieldsByDisplayMode(step.fields, displayMode),
      })).filter((step) => step.fields.length > 0);
    }

    return formDef;
  }, [schema, loading, displayMode]);

  return {
    formDefinition,
    loading,
    error,
    refetch,
  };
}
